import React, { useRef, useContext, use } from "react";
import { useMutation, useQuery } from "@apollo/client";

import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";

import logo from "../../../assets/images/logo.svg";
import { toastError, toastSuccess, toastWarning } from "../../../shared/helpers";
import {
  FILL_REFERRAL_FORM,
  GET_REFERRAL_FORM_BY_URL,
} from "../../../shared/queries";
import { PrimaryButton } from "../../atoms/Buttons";
import { FormCard } from "../../atoms/Cards";
import { DarkFormField } from "../../atoms/Inputs";
import { H6, P } from "../../atoms/Texts";
import { ReferralFormsLayout } from "../../organisms/Layouts";
import FormGenerator from "../../molecules/ModernFormBuilder/FormGenerator";

import { demoContext } from "../../../shared/store/contexts";

const ReferralFormPage = () => {
  const loader = useRef(null);
  const { url } = useParams();

  const { referralForms } = useContext(demoContext);

  const [fillForm] = useMutation(FILL_REFERRAL_FORM);

  const formCtx = useForm({
    mode: "onSubmit",
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = formCtx;

  const submitForm = async (data) => {
    if (loader) {
      loader.current.continuousStart();
    }

    toastWarning("Can not perform this action on demo mode");
  };

  const formFields = referralForms[0]?.formFields
    ? JSON.parse(referralForms[0]?.formFields)
    : [];

  return (
    <ReferralFormsLayout>
      <LoadingBar height={2} color="#00AF72" ref={loader} />
      <section className="h-full flex justify-center items-center">
        <FormProvider {...formCtx}>
          <form
            onSubmit={handleSubmit(submitForm)}
            className="pb-10 w-full mx-8 md:w-1/2 md:mx-0"
          >
            <FormCard className="px-4 py-8 md:py-14 mx-auto w-full">
              <div className="mx-auto" style={{ width: "fit-content" }}>
                <img src={logo} alt="SocialSafety" className="mb-12" />
              </div>

              <>
                <H6
                  data-testid="create-account-text"
                  color="#22202D"
                  fontSize="18px"
                  className="mb-0"
                >
                  {referralForms[0]?.service?.name}
                </H6>
                <P color="#000" fontSize="14px" className="">
                  By {referralForms[0]?.service?.organization?.name}
                </P>
                <div className="border-b border-gray-300 my-4"></div>

                <div className="flex flex-col gap-y-4">
                  <div>
                    <DarkFormField
                      type="text"
                      id="firstName"
                      name="firstName"
                      color="#F8F6FE"
                      className="py-1 px-2"
                      label="First Name"
                      placeholder="Enter your first name"
                      inputClassName="-mx-1"
                      required
                      {...register("firstName", {
                        required: "Please enter your first name",
                      })}
                    />
                    {errors.firstName && (
                      <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                        {errors.firstName.message}
                      </P>
                    )}
                  </div>

                  <div>
                    <DarkFormField
                      type="text"
                      id="lastName"
                      name="lastName"
                      color="#F8F6FE"
                      className="py-1 px-2"
                      label="Last Name"
                      placeholder="Enter your last name"
                      inputClassName="-mx-1"
                      required
                      {...register("lastName", {
                        required: "Please enter your last name",
                      })}
                    />
                    {errors.lastName && (
                      <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                        {errors.lastName.message}
                      </P>
                    )}
                  </div>

                  <div>
                    <DarkFormField
                      type="email"
                      id="email"
                      name="email"
                      w="135px"
                      placeholder="example@mail.com"
                      label="Email"
                      className="w-full"
                      required
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value:
                            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                          message: "Please enter a valid email.",
                        },
                      })}
                    />
                    {errors.email && (
                      <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                        {errors.email.message}
                      </P>
                    )}
                  </div>

                  <FormGenerator fields={formFields} />
                </div>

                <div className="my-10 block mx-auto">
                  <PrimaryButton className="w-full" type="submit">
                    Submit Form
                  </PrimaryButton>
                </div>
              </>
            </FormCard>
          </form>
        </FormProvider>
      </section>
    </ReferralFormsLayout>
  );
};

export default ReferralFormPage;
